import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueForecastPanel = ({ forecast, loading, onRefresh }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!forecast) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Revenue Forecast</h3>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Icon name="TrendingUp" size={48} className="mb-2" />
          <p className="mb-4">No forecast data available</p>
          <Button onClick={onRefresh} variant="outline" iconName="RefreshCw" iconPosition="left">
            Generate Forecast
          </Button>
        </div>
      </div>
    );
  }

  const getTrendIcon = (trend) => {
    if (trend === 'up') return 'TrendingUp';
    if (trend === 'down') return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-success';
    if (trend === 'down') return 'text-destructive';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Icon name="Brain" className="text-primary" size={20} />
            <h3 className="text-lg font-semibold text-foreground">AI Revenue Forecast</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Powered by OpenAI GPT-5</p>
        </div>
        <Button onClick={onRefresh} variant="ghost" size="sm" iconName="RefreshCw" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-accent/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Next 7 Days</p>
            <Icon name={getTrendIcon(forecast?.short_term?.trend)} className={getTrendColor(forecast?.short_term?.trend)} size={16} />
          </div>
          <p className="text-2xl font-bold text-foreground">${forecast?.short_term?.predicted_revenue?.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Confidence: {(forecast?.short_term?.confidence * 100)?.toFixed(0)}%
          </p>
        </div>

        <div className="bg-accent/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Next 30 Days</p>
            <Icon name={getTrendIcon(forecast?.medium_term?.trend)} className={getTrendColor(forecast?.medium_term?.trend)} size={16} />
          </div>
          <p className="text-2xl font-bold text-foreground">${forecast?.medium_term?.predicted_revenue?.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Confidence: {(forecast?.medium_term?.confidence * 100)?.toFixed(0)}%
          </p>
        </div>

        <div className="bg-accent/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Next 90 Days</p>
            <Icon name={getTrendIcon(forecast?.long_term?.trend)} className={getTrendColor(forecast?.long_term?.trend)} size={16} />
          </div>
          <p className="text-2xl font-bold text-foreground">${forecast?.long_term?.predicted_revenue?.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Confidence: {(forecast?.long_term?.confidence * 100)?.toFixed(0)}%
          </p>
        </div>
      </div>

      {forecast?.insights && forecast?.insights?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" className="text-primary mt-0.5" size={18} />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-2">AI Insights</h4>
              <ul className="space-y-1">
                {forecast?.insights?.slice(0, showDetails ? undefined : 3)?.map((insight, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
              {forecast?.insights?.length > 3 && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-primary hover:underline mt-2"
                >
                  {showDetails ? 'Show less' : `Show ${forecast?.insights?.length - 3} more insights`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {forecast?.recommendations && forecast?.recommendations?.length > 0 && (
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="text-success mt-0.5" size={18} />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-2">Recommendations</h4>
              <ul className="space-y-1">
                {forecast?.recommendations?.map((rec, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-success mt-1">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          Forecast generated at {new Date(forecast?.generated_at)?.toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default RevenueForecastPanel;